import React, { type RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { NavBar } from './NavBar';
import { StatsRow } from './StatsRow';
import { heroStatement, heroStats } from '../data/heroStats';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileHeroSweep } from './MobileHeroSweep';
import { INTRO, focusIn, logoFocus, wordmarkFocus } from '../utils/reveal';
interface HeroSectionProps {
  /** The drone anchors itself to the middle of this section */
  sectionRef: RefObject<HTMLElement>;
}

/** Statement top left, logo centered, stats along the bottom; the drone floats in the middle. */
export function HeroSection({
  sectionRef
}: HeroSectionProps) {
  const reduce = useReducedMotion();
  // Phones: the intro is one diagonal sweep over the whole hero (see MobileHeroSweep)
  const mobile = useIsMobile();
  return (
    // Mobile: fills the whole screen; desktop: 80% of the window height
    <section ref={sectionRef} data-drone-fixed="true" className="relative flex h-[100svh] min-h-[560px] w-full flex-col overflow-hidden bg-white text-black md:h-[80vh] md:min-h-[600px]" data-id="element-101" data-mp="3kauo3qpr5e7n">
      <NavBar data-id="element-102" data-mp="2bf856eq18da4" />
      {/* Phones: the whole hero is revealed in one diagonal sweep, bottom-left → top-right */}
      {mobile && !reduce && <MobileHeroSweep data-id="element-103" data-mp="2p7ejdv9mka6x" />}

      {/* Centered logo, top — matches the reference at 1720px: ~107px tall, ~20px from the top */}
      {/* Mobile: slightly smaller, centred at the top with the contact pill below it */}
      <div data-drone-push className="absolute left-1/2 top-6 z-30 -translate-x-1/2 md:top-[clamp(0.75rem,1.2vw,1.5rem)]" data-id="element-104" data-mp="2u4es6dxuzr1q">
        {/* key: swap animation variant cleanly if the layout crosses the breakpoint */}
        <motion.a key={mobile ? 'm' : 'd'} href="#" className="block" {...mobile ? {} : logoFocus(INTRO.logo, reduce)} data-id="element-105" data-mp="334l2jqy9gawo">
          <img src="https://cdn.magicpatterns.com/uploads/iFCAPKP6Hif5VAC8rezXvk/image.png" alt="Laebel home"
          // White-on-black artwork: inverted to black, and multiply drops its (now white) backdrop
          className="h-[5rem] w-auto invert mix-blend-multiply md:h-[clamp(5rem,6.9vw,8.4rem)]" data-id="element-106" data-mp="1drzqb2kje2h3" />
        </motion.a>
      </div>

      {/* Mobile: agency tagline centred beneath the logo (24px top + 80px logo + 36px gap) */}
      <p data-drone-push className="absolute inset-x-0 top-[8.75rem] z-30 overflow-hidden px-3 pb-[0.12em] text-center text-base font-bold leading-tight tracking-[-0.03em] text-black md:hidden" data-id="element-107" data-mp="3munnxw7wrw6p">
        <motion.span className="block" data-id="element-108" data-mp="jt6g2ni0qw9">
          {heroStats[0]}
        </motion.span>
      </p>

      {/* Top-left block — proportions from the reference at 1720px: ~27px statement, ~54px pill */}
      {/* Hidden visually on mobile (the logo sits here instead); kept for screen readers */}
      <div className="sr-only md:not-sr-only md:absolute md:left-3 md:top-3 md:z-30" data-id="element-109" data-mp="ef3vv0iz4jt8">
        <h1 className="text-[clamp(1.25rem,1.6vw,1.9rem)] font-semibold leading-[1.12] tracking-[-0.045em]" data-id="element-110" data-mp="jcx19rcgd9sg">
          {/* Each line comes into focus in turn (and can be knocked about by a hypnotised Pixel) */}
          {heroStatement.map((line, li) => <motion.span key={line} data-drone-push className="block w-fit" {...focusIn(INTRO.statement + li * INTRO.line, reduce)} data-id="element-111" data-mp="1ut84kuyo61ss" data-mpi={li}>
              {line}
            </motion.span>)}
        </h1>
      </div>

      {/* Giant edge-to-edge wordmark, vertically centered behind the drone (reference: ~22px gutters at 1720px) */}
      {/* Mobile: the wordmark turns vertical, centred and stretched from the top to the bottom edge */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 z-10 -translate-x-1/2 py-2 md:hidden" aria-hidden="true" data-id="element-112" data-mp="2dr18o0l14hrr">
        <motion.svg viewBox="-206 0 210 1000" preserveAspectRatio="xMidYMid meet" className="block h-full w-auto" focusable="false" data-id="element-113" data-mp="28rojvaall0mj">
          <text x="0" y="198" transform="rotate(90)" textLength="1000" lengthAdjust="spacingAndGlyphs" className="fill-black [fill-opacity:0.03]" style={{
            fontFamily: "'Clash Display', sans-serif",
            fontWeight: 700,
            fontSize: 280,
            letterSpacing: '-0.02em'
          }} data-id="element-114" data-mp="31g3n6zkd9ui1">
            LAEBEL’
          </text>
        </motion.svg>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 hidden -translate-y-1/2 overflow-hidden px-[1.28vw] md:block" aria-hidden="true" data-id="element-115" data-mp="34qmzm6b8f9i1">
        <motion.svg viewBox="0 -4 1000 210" className="block h-auto w-full" focusable="false" {...wordmarkFocus(INTRO.wordmark, reduce)} data-id="element-116" data-mp="38uckolsp78yb">
          <text x="0" y="198" textLength="1000" lengthAdjust="spacingAndGlyphs"
          // Mobile: 3% (the small wordmark all but disappears at 1.5%); desktop stays at 1.5%
          className="fill-black [fill-opacity:0.03] md:[fill-opacity:0.015]" style={{
            fontFamily: "'Clash Display', sans-serif",
            fontWeight: 700,
            fontSize: 280,
            letterSpacing: '-0.02em'
          }} data-id="element-117" data-mp="2zy3pjjbr6whz">
            LAEBEL’
          </text>
        </motion.svg>
      </div>

      {/* Proof points along the bottom, just above the showreel */}
      <div className="absolute inset-x-0 bottom-14 z-30 md:bottom-[clamp(1rem,2vw,2rem)]" data-id="element-118" data-mp="ahofuzeho4tf">
        <StatsRow data-id="element-119" data-mp="3ix85lofyb53q" />
      </div>

      {/* The drone and its chat live in a page-wide layer (DroneCompanion) anchored to this section */}
    </section>
  );
}